import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useTran } from 'src/i18n/useTran'
import { msg } from 'src/i18n/texts/keys'
import { infoMessagesStore } from 'src/context/InfoMessageContext'
import { Infomessage } from 'src/api/defs/Infomessage.type'
import { useFlushImportantMessagesWithSessionStorage } from './useFlushImportantMessagesWithSessionStorage'
import InfoToaster from 'src/ui/alert/InfoToaster'
import { getAppContext } from 'src/context/AppContext'

export const IMFO_MESSAGES_STORAGE_KEY = 'infoMessages'

const getContractWarehouseName = (c: any): string | null =>
    c?.warehouse?.name || c?.warehouseName || c?.box?.warehouse?.name || null

const BOLD_OPEN_TOKEN = '__INFO_BOLD_OPEN__'
const BOLD_CLOSE_TOKEN = '__INFO_BOLD_CLOSE__'

type InlinePart = {
    text: string
    bold: boolean
}

const sanitizeMessageText = (value: string): string =>
    value
        .replace(/<br\s*\/?>\s*(\r\n|\r|\n)/gi, '\n')
        .replace(/\r\n?/g, '\n')
        .replace(/\s*\\n\s*/g, '\n')
        .replace(/\s*\/n\s*/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\s*\/\s*p\s*>\s*<\s*p\s*>/gi, '\n\n')
        .replace(/<\s*p\s*>/gi, '')
        .replace(/<\s*\/\s*p\s*>/gi, '\n')
        .replace(/<\s*(b|strong)\s*>/gi, BOLD_OPEN_TOKEN)
        .replace(/<\s*\/\s*(b|strong)\s*>/gi, BOLD_CLOSE_TOKEN)
        .replace(/<[^>]*>?/gm, '')
        .replace(/[ \t]*\n[ \t]*/g, '\n')

const splitInlineParts = (value: string): InlinePart[] => {
    const parts: InlinePart[] = []
    let cursor = 0
    let isTagBold = false

    const pushPart = (text: string, bold: boolean) => {
        if (!text) return

        const prev = parts[parts.length - 1]
        if (prev && prev.bold === bold) {
            prev.text += text
            return
        }

        parts.push({ text, bold })
    }

    while (cursor < value.length) {
        if (value.startsWith(BOLD_OPEN_TOKEN, cursor)) {
            isTagBold = true
            cursor += BOLD_OPEN_TOKEN.length
            continue
        }
        if (value.startsWith(BOLD_CLOSE_TOKEN, cursor)) {
            isTagBold = false
            cursor += BOLD_CLOSE_TOKEN.length
            continue
        }

        if (value.startsWith('**', cursor)) {
            const close = value.indexOf('**', cursor + 2)
            if (close !== -1) {
                const boldText = value.slice(cursor + 2, close)
                pushPart(boldText, true)
                cursor = close + 2
                continue
            }
        }

        let next = value.length
        const nextOpen = value.indexOf(BOLD_OPEN_TOKEN, cursor)
        const nextClose = value.indexOf(BOLD_CLOSE_TOKEN, cursor)
        const nextMarkdownBold = value.indexOf('**', cursor)

        if (nextOpen !== -1) next = Math.min(next, nextOpen)
        if (nextClose !== -1) next = Math.min(next, nextClose)
        if (nextMarkdownBold !== -1) next = Math.min(next, nextMarkdownBold)

        if (next === cursor) {
            pushPart(value.slice(cursor, cursor + 2), isTagBold)
            cursor += 2
            continue
        }

        pushPart(value.slice(cursor, next), isTagBold)
        cursor = next
    }

    return parts
}

const renderFormattedMessage = (value: string, keyPrefix: string) => {
    const sanitized = sanitizeMessageText(value)
    const lines = sanitized.split('\n')

    return lines.map((line, lineIndex) => {
        const lineParts = splitInlineParts(line.trim())

        return (
            <React.Fragment key={`${keyPrefix}-line-${lineIndex}`}>
                {lineParts.map((part, partIndex) =>
                    part.bold ? (
                        <strong
                            key={`${keyPrefix}-part-${lineIndex}-${partIndex}`}
                        >
                            {part.text}
                        </strong>
                    ) : (
                        <React.Fragment
                            key={`${keyPrefix}-part-${lineIndex}-${partIndex}`}
                        >
                            {part.text}
                        </React.Fragment>
                    )
                )}
                {lineIndex < lines.length - 1 && <br />}
            </React.Fragment>
        )
    })
}

const normalizeName = (s: string | null | undefined): string | null =>
    typeof s === 'string' ? s.trim().toLowerCase() : null

const InfoMessages: React.FC = () => {
    const [infoMessages, setInfoMessages] = useState<Infomessage[] | null>(null)
    const [unreadMessages, setUnreadMessages] = useState<Infomessage[]>([])
    const [open, setOpen] = useState(false)

    const t = useTran()
    const location = useLocation()
    const ctx = getAppContext()

    useFlushImportantMessagesWithSessionStorage()

    useEffect(() => {
        const sub = infoMessagesStore.changes().subscribe(messages => {
            setInfoMessages(messages)
        })
        return () => sub.unsubscribe()
    }, [])

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            try {
                const stored =
                    localStorage.getItem(IMFO_MESSAGES_STORAGE_KEY) || '[]'
                const seen = JSON.parse(stored) as Pick<
                    Infomessage,
                    'id' | 'important'
                >[]

                if (!infoMessages || infoMessages.length === 0) {
                    if (!cancelled) {
                        setUnreadMessages([])
                        setOpen(false)
                    }
                    return
                }

                const path = location.pathname
                const isMyBoxesPage = path === '/my/boxes'
                const contractMatch = path.match(/^\/my\/contracts\/(\d+)/)
                const isMyContractDetail = !!contractMatch
                const contractId = contractMatch
                    ? Number(contractMatch[1])
                    : null
                const warehouseMatch = path.match(/^\/location\/(\d+)/)
                const isWarehouseDetail = !!warehouseMatch
                const warehouseId = warehouseMatch
                    ? Number(warehouseMatch[1])
                    : null

                const baseUnread = infoMessages.filter(m => {
                    const isWarehouseScopedOnDetailPage =
                        isWarehouseDetail &&
                        warehouseId != null &&
                        m.warehouseId === warehouseId

                    if (isWarehouseScopedOnDetailPage) return true

                    return !seen.some(s => s.id === m.id)
                })

                if (baseUnread.length === 0) {
                    if (!cancelled) {
                        setUnreadMessages([])
                        setOpen(false)
                    }
                    return
                }

                const oznamCandidates = baseUnread.filter(
                    m => m.important && m.warehouseId != null
                )

                const warehouseNameById = new Map<number, string>()

                if (oznamCandidates.length > 0) {
                    await Promise.all(
                        oznamCandidates.map(async m => {
                            if (!m.warehouseId) return
                            try {
                                const detail = await ctx.warehouse.api.detail(
                                    m.warehouseId
                                )
                                if (detail?.name) {
                                    warehouseNameById.set(
                                        m.warehouseId,
                                        detail.name
                                    )
                                }
                            } catch {}
                        })
                    )
                }

                let scoped = baseUnread

                if (oznamCandidates.length > 0) {
                    if (isMyBoxesPage) {
                        try {
                            const contracts = await ctx.myZoneApi.contracts(
                                {} as any
                            )
                            const contractNames = new Set(
                                contracts
                                    .map(getContractWarehouseName)
                                    .map(normalizeName)
                                    .filter(Boolean) as string[]
                            )

                            scoped = baseUnread.filter(m => {
                                if (!m.warehouseId) return true

                                const whNameRaw = warehouseNameById.get(
                                    m.warehouseId
                                )
                                const whName = normalizeName(whNameRaw)
                                if (!whName) return false
                                return contractNames.has(whName)
                            })
                        } catch {
                            scoped = baseUnread.filter(
                                m => m.warehouseId == null
                            )
                        }
                    } else if (isMyContractDetail && contractId) {
                        try {
                            const contract =
                                await ctx.myZoneApi.contract(contractId)
                            const contractName = normalizeName(
                                getContractWarehouseName(contract)
                            )

                            scoped = baseUnread.filter(m => {
                                if (!m.warehouseId) return true
                                const whNameRaw = warehouseNameById.get(
                                    m.warehouseId
                                )
                                const whName = normalizeName(whNameRaw)
                                if (!whName || !contractName) return false
                                return whName === contractName
                            })
                        } catch {
                            scoped = baseUnread.filter(
                                m => m.warehouseId == null
                            )
                        }
                    } else if (isWarehouseDetail && warehouseId) {
                        scoped = baseUnread.filter(m => {
                            if (!m.warehouseId) return true
                            return m.warehouseId === warehouseId
                        })
                    } else {
                        scoped = baseUnread.filter(m => m.warehouseId == null)
                    }
                }

                if (cancelled) return
                setUnreadMessages(scoped)
                setOpen(scoped.length > 0)
            } catch {
                if (!cancelled) {
                    if (infoMessages && infoMessages.length > 0) {
                        setUnreadMessages(infoMessages)
                        setOpen(true)
                    } else {
                        setOpen(false)
                    }
                }
            }
        }

        run()
        return () => {
            cancelled = true
        }
    }, [infoMessages, location.pathname, ctx])

    const markAllSeen = () => {
        if (!unreadMessages || unreadMessages.length === 0) return

        const stored = localStorage.getItem(IMFO_MESSAGES_STORAGE_KEY) || '[]'
        const seen = JSON.parse(stored) as Pick<
            Infomessage,
            'id' | 'important'
        >[]

        const add = unreadMessages.map(m => ({
            id: m.id,
            important: m.important
        }))

        const byId = new Map<number, Pick<Infomessage, 'id' | 'important'>>()
        ;[...seen, ...add].forEach(m => byId.set(m.id, m))

        localStorage.setItem(
            IMFO_MESSAGES_STORAGE_KEY,
            JSON.stringify(Array.from(byId.values()))
        )
    }

    const handleClose = () => {
        markAllSeen()
        setOpen(false)
    }

    if (!open || unreadMessages.length === 0) return null

    const hasImportant = unreadMessages.some(m => m.important)

    const title = t(msg.ui.infoMsg.oznamTitle)

    const tone = hasImportant ? 'warning' : 'info'
    const kind = 'system' as const

    const textNode = (
        <div>
            {unreadMessages.map(m => (
                <p key={m.id} style={{ marginBottom: 8 }}>
                    {renderFormattedMessage(m.text, `msg-${m.id}`)}
                </p>
            ))}
        </div>
    )

    return (
        <InfoToaster
            open={open}
            onClose={handleClose}
            tone={tone}
            kind={kind}
            title={title}
            text={textNode}
            primaryCta={{
                label: t(msg.Infomessage.modal.button),
                onClick: handleClose
            }}
        />
    )
}

export default InfoMessages
